import { Body, CaretSvg, colors, Dropdown, Icon, Margin, Spacer } from '@spritz-finance/ui'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useIntercom } from 'react-use-intercom'
import styled, { css } from 'styled-components'
import logo from '../../assets/img/primary-logo.svg'
import { useCurrentUser } from '../../context/useCurrentUser'
import { Feature, useFeatureFlag } from '../../context/useFeatureFlag'
import { useFeedback } from '../../context/useFeedback'
import { useScreenSize } from '../../context/useScreenSize'
import { useLogout } from '../../modules/app/auth'
import { cssVar, themedImage } from '../../theme/utils'
import { formatCurrency } from '../../utils/format'
import { Wallet } from '../../web3/Wallet'
import { Row } from '../Layout/Row'
import { UserAvatar } from '../UserAvatar'
import { MobileMenu } from './MobileMenu'
import { NewBadge } from '../badges/BadgeContainer'

export const TopNavWrapper = styled.div<{ $height?: number }>`
    height: ${({ $height }) => $height ?? 90}px;
    background-color: var(--background-secondary, ${colors.white});
    box-shadow: 4px 4px 15px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: row;
    z-index: 2;
`

export const NavContent = styled.div<{ $mobile?: boolean }>`
    max-width: 1300px;
    display: flex;
    flex: 1;
    margin: 0 auto;
    padding: 0 ${({ $mobile }) => ($mobile ? Margin.Small : Margin.Med)};
    justify-content: space-between;
`

const Inner = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`

const Logo = styled.img`
    height: var(--logo-height, 34px);
    margin-right: 64px;
`

const TopNavLink = styled(Link)<{ disabled?: boolean; $active?: boolean }>`
    text-decoration: none;
    margin-right: 48px;
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    cursor: pointer;
    transition: 0.25s color ease-in-out;
    color: var(--text-primary, ${colors.black});

    ${(props) =>
        props.disabled &&
        css`
            opacity: 0.2;
            cursor: default;
        `}

    ${({ $active }) =>
        !$active &&
        css`
            color: var(--text-secondary, ${colors.medGrey});
        `}
`

const TopPlainLink = styled.a<{ disabled?: boolean }>`
    text-decoration: none;
    margin-right: 80px;
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    cursor: pointer;
    color: var(--text-primary, ${colors.black});

    ${(props) =>
        props.disabled &&
        css`
            opacity: 0.2;
            cursor: default;
        `}
`

const RewardsLink = styled(Link)<{ disabled?: boolean; $active: boolean }>`
    text-decoration: none;
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    cursor: pointer;
    color: var(--text-primary, ${colors.black});

    padding: 6px 10px;
    position: relative;

    transition: 0.25s all ease-in-out;
    display: flex;
    align-items: center;

    .balance {
        margin-left: 10px;
    }

    ${(props) =>
        props.disabled &&
        css`
            opacity: 0.2;
            cursor: default;
        `}
    ${({ $active }) =>
        !$active &&
        css`
            color: var(--text-secondary, ${colors.medGrey});
        `}
`

const SupportMenu = styled.div`
    margin-right: 48px;
`

const TopNavNewBadge = styled.div`
    position: relative;
    top: -8px;
    margin-left: 8px;
`

const UserMenu = () => {
    const { me } = useCurrentUser()
    const navigate = useNavigate()
    const { logout } = useLogout()

    const options = [
        { title: 'Profile', onClick: () => navigate('/profile') },
        { title: 'Sign Out', onClick: logout },
    ]

    return (
        <Dropdown.Menu
            minWidth={160}
            align="end"
            anchor={
                <Inner style={{ cursor: 'pointer' }}>
                    <UserAvatar user={me} size={45} />
                    <Spacer width={8} />
                    {!!me.firstName && <Body.SmBold size={16}>{me.firstName}</Body.SmBold>}
                </Inner>
            }
        >
            {options.map((option, index) => {
                return (
                    <Dropdown.Item onClick={option.onClick} key={index}>
                        <Row align="space-between">
                            <Body.Med>{option.title}</Body.Med>
                        </Row>
                    </Dropdown.Item>
                )
            })}
        </Dropdown.Menu>
    )
}

export const TopNav = () => {
    const { openFeedbackForm } = useFeedback()
    const location = useLocation()
    const { isDesktop, isMobile } = useScreenSize()
    const { show: openIntercom } = useIntercom()

    const { growsurf } = useCurrentUser()
    const balance = growsurf?.rewardsBalance ?? 0
    const isCardActive = useFeatureFlag(Feature.SpritzCard)

    const contactSupport = () => openIntercom()

    const { pathname } = location
    const isActive = (link: string) => pathname.startsWith(link)

    if (isDesktop) {
        return (
            <TopNavWrapper>
                <NavContent>
                    <Inner>
                        <Logo src={themedImage('appLogo', logo)} />

                        <TopNavLink $active={isActive('/bills')} to={'/bills'}>
                            Pay
                        </TopNavLink>

                        {isCardActive && (
                            <TopNavLink $active={isActive('/card')} to={'/card'}>
                                <Row>
                                    Spend
                                    <TopNavNewBadge>
                                        <NewBadge />
                                    </TopNavNewBadge>
                                </Row>
                            </TopNavLink>
                        )}

                        <TopNavLink $active={isActive('/offramp')} to={'/offramp'}>
                            Off-ramp
                        </TopNavLink>

                        <Dropdown.Menu
                            minWidth={160}
                            align="end"
                            anchor={
                                <SupportMenu>
                                    <Inner style={{ cursor: 'pointer' }}>
                                        <Body.SmBold
                                            size={16}
                                            color={cssVar('--text-secondary', colors.medGrey)}
                                        >
                                            Support
                                        </Body.SmBold>
                                        <Icon
                                            icon={CaretSvg}
                                            size={24}
                                            tint={cssVar('--text-secondary', colors.charcoal)}
                                        />
                                    </Inner>
                                </SupportMenu>
                            }
                        >
                            <Dropdown.Item key="a">
                                <TopPlainLink
                                    target="_blank"
                                    href="https://help.spritz.finance/en/"
                                >
                                    <Row align="space-between">
                                        <Body.Med>Help center</Body.Med>
                                    </Row>
                                </TopPlainLink>
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => contactSupport()}>
                                <Row align="space-between">
                                    <Body.Med>Contact support</Body.Med>
                                </Row>
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => openFeedbackForm()}>
                                <Row align="space-between">
                                    <Body.Med>Share feedback</Body.Med>
                                </Row>
                            </Dropdown.Item>
                        </Dropdown.Menu>

                        <RewardsLink $active={isActive('/referrals')} to={'/referrals/main'}>
                            <span className="main">
                                Rewards
                                {balance > 0 && (
                                    <span className="balance">{formatCurrency(balance)}</span>
                                )}
                            </span>
                        </RewardsLink>
                    </Inner>
                    <Inner>
                        <UserMenu />
                        <Spacer width={32} />
                        <Wallet />
                    </Inner>
                </NavContent>
            </TopNavWrapper>
        )
    }

    return (
        <TopNavWrapper $height={isMobile ? 56 : undefined}>
            <NavContent $mobile={isMobile}>
                <Inner>
                    <MobileMenu />
                </Inner>
                <Inner>
                    <Wallet responsive />
                </Inner>
            </NavContent>
        </TopNavWrapper>
    )
}
